/**
 * Cart Module
 *
 * localStorage-based cart with support for Shoppex features:
 * - Addons (express shipping, gift wrap, etc.)
 * - Custom Fields (engraving, gift message, etc.)
 * - Price Variants (different pricing tiers)
 */

import { getItem, setItem, removeItem } from '../utils/storage';
import { getConfig } from '../core/config';
import { post } from '../core/client';
import { CartError } from '../core/errors';
import type {
  CartItem,
  CartAddOptions,
  CartItemUpdate,
  CartPayload,
  CartMetadata,
  CartStats,
  CartQuote,
  CartBasketMergeLine,
  CartCodeSource,
} from '../types';
import { getAffiliateCode, trackAffiliateEvent } from './affiliates';
import { computeCartLineId, ensureCartLineId } from '../utils/cart-line-id';
import {
  normalizeRequestedCurrency,
  resolveBuyerCurrency,
} from '../utils/requested-currency';
import { roundPayableAmount } from '@shoppex/contracts/catalog-unit-price';

const STORAGE_KEYS = {
  cart: 'cart',
  cartBackup: 'cart_backup',
  meta: 'cart_meta',
  metaBackup: 'cart_backup_meta',
  coupon: 'cart_coupon',
  couponBackup: 'cart_coupon_backup',
} as const;

type StorageKeyType = keyof typeof STORAGE_KEYS;

function getStorageKey(type: StorageKeyType): string {
  return `${STORAGE_KEYS[type]}_${getConfig().storeSlug}`;
}

function hashString(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16);
}

function computeChecksum(cart: CartItem[]): string {
  return hashString(JSON.stringify(cart));
}

function normalizeQuantity(value: number): number {
  if (!Number.isFinite(value)) {
    throw new CartError('quantity must be a finite number');
  }
  return Math.floor(value);
}

function normalizeCouponCode(value: string | null | undefined): string | null {
  const normalized = value?.trim().toUpperCase();
  return normalized ? normalized : null;
}

type StoredCartCode = {
  code: string;
  source: CartCodeSource;
};

function getStoredCartCode(): StoredCartCode | null {
  const raw = getItem<unknown>(getStorageKey('coupon'));
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const code = typeof record.code === 'string' ? normalizeCouponCode(record.code) : null;
  const source = record.source === 'COUPON' || record.source === 'AFFILIATE'
    ? record.source
    : null;
  return code && source ? { code, source } : null;
}

function normalizeCartItems(value: unknown): CartItem[] {
  if (!Array.isArray(value)) return [];
  const normalized: CartItem[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const productId = typeof record.product_id === 'string' ? record.product_id.trim() : '';
    const variantId = typeof record.variant_id === 'string' ? record.variant_id.trim() : '';
    const quantity = Number(record.quantity);

    if (!productId || !variantId || !Number.isFinite(quantity) || quantity < 1) {
      continue;
    }

    const item: CartItem = {
      line_id: '',
      product_id: productId,
      variant_id: variantId,
      quantity: Math.floor(quantity),
    };

    if (typeof record.price_variant_id === 'string') {
      item.price_variant_id = record.price_variant_id;
    }
    if (record.price_data && typeof record.price_data === 'object') {
      const priceData = record.price_data as Record<string, unknown>;
      if (typeof priceData.unit_price === 'number' && Number.isFinite(priceData.unit_price)) {
        item.price_data = { unit_price: priceData.unit_price };
      }
    }
    if (typeof record.pay_what_you_want_price === 'number' && Number.isFinite(record.pay_what_you_want_price)) {
      item.pay_what_you_want_price = record.pay_what_you_want_price;
    }
    if (Array.isArray(record.addons)) {
      item.addons = record.addons as CartItem['addons'];
    }
    if (record.custom_fields && typeof record.custom_fields === 'object' && !Array.isArray(record.custom_fields)) {
      item.custom_fields = record.custom_fields as Record<string, string>;
    }

    // Preserve the optional display snapshot (title/variant_title/image_url/addon_labels) across the
    // localStorage round-trip so the njk cart drawer/page can render without a product fetch.
    if (typeof record.title === 'string') {
      item.title = record.title;
    }
    if (typeof record.variant_title === 'string') {
      item.variant_title = record.variant_title;
    }
    if (typeof record.image_url === 'string') {
      item.image_url = record.image_url;
    }
    if (Array.isArray(record.addon_labels)) {
      item.addon_labels = record.addon_labels.filter((label): label is string => typeof label === 'string');
    }
    if (typeof record.max_quantity === 'number' && Number.isFinite(record.max_quantity)) {
      item.max_quantity = record.max_quantity;
    }
    if (typeof record.min_quantity === 'number' && Number.isFinite(record.min_quantity)) {
      item.min_quantity = record.min_quantity;
    }

    // Self-heal on READ: clamp quantity to the stored [min,max] here, after the bounds are read. This is
    // the single chokepoint every access flows through (getCart -> getCartPayload -> serializeCart/
    // quoteCart -> checkout), so a stale/tampered/restored localStorage line can never reach checkout
    // out of bounds — closing restoreCartFromBackup and direct-localStorage-edit at the root, not just
    // the write paths.
    clampQuantityToBounds(item);

    const storedLineId = typeof record.line_id === 'string' ? record.line_id.trim() : '';
    item.line_id = storedLineId || computeCartLineId(item);

    normalized.push(ensureCartLineId(item));
  }
  return normalized;
}

function getCartMetadata(): CartMetadata | null {
  return getItem<CartMetadata>(getStorageKey('meta'));
}

export function getCartCoupon(): string | null {
  return getStoredCartCode()?.code ?? null;
}

export function getCartCouponSource(): CartCodeSource | null {
  return getStoredCartCode()?.source ?? null;
}

export function setCartCoupon(
  coupon: string | null | undefined,
  source: CartCodeSource = 'COUPON',
): string | null {
  const normalizedCoupon = normalizeCouponCode(coupon);
  if (!normalizedCoupon) {
    removeItem(getStorageKey('coupon'));
    return null;
  }

  setItem(getStorageKey('coupon'), { code: normalizedCoupon, source });
  return normalizedCoupon;
}

export function clearCartCoupon(): void {
  removeItem(getStorageKey('coupon'));
}

function writeCart(cart: CartItem[]): void {
  const normalizedCart = normalizeCartItems(cart);
  setItem(getStorageKey('cart'), normalizedCart);
  const now = Date.now();
  const previous = getCartMetadata();
  const nextMeta: CartMetadata = {
    created_at: previous?.created_at ?? now,
    last_modified: now,
    version: (previous?.version ?? 0) + 1,
    checksum: computeChecksum(normalizedCart),
  };
  setItem(getStorageKey('meta'), nextMeta);

  if (normalizedCart.length === 0) {
    clearCartCoupon();
  }
}

function setCartWithMetadata(cart: CartItem[], metadata?: CartMetadata | null): void {
  const normalizedCart = normalizeCartItems(cart);
  setItem(getStorageKey('cart'), normalizedCart);
  const now = Date.now();
  const base = metadata ?? getCartMetadata();
  const nextMeta: CartMetadata = {
    created_at: base?.created_at ?? now,
    last_modified: now,
    version: base?.version ?? 1,
    checksum: computeChecksum(normalizedCart),
  };
  setItem(getStorageKey('meta'), nextMeta);
}

export function getCart(): CartItem[] {
  const raw = getItem<unknown>(getStorageKey('cart'));
  return normalizeCartItems(raw);
}

/** Resolve a cart line_id for product+variant. When multiple configs exist, returns the first line. */
export function resolveCartLineId(
  productId: string,
  variantId: string,
  cart: CartItem[] = getCart(),
): string {
  const matches = cart.filter(
    (item) => item.product_id === productId && item.variant_id === variantId,
  );
  if (matches.length === 0) {
    throw new CartError(`No cart line found for ${productId}/${variantId}`);
  }
  return matches[0].line_id;
}

export function getCartItemCount(): number {
  const cart = getCart();
  return cart.reduce((sum, item) => sum + item.quantity, 0);
}

export function addToCart(
  productId: string,
  variantId: string,
  quantity: number = 1,
  options?: CartAddOptions
): void {
  if (!productId || !variantId) {
    throw new CartError('product_id and variant_id are required');
  }

  const normalizedQuantity = normalizeQuantity(quantity);
  if (normalizedQuantity < 1) {
    throw new CartError('quantity must be at least 1');
  }

  const cart = getCart();

  const lineId = computeCartLineId({
    product_id: productId,
    variant_id: variantId,
    addons: options?.addons,
    custom_fields: options?.custom_fields,
    price_variant_id: options?.price_variant_id,
    price_data: options?.price_data,
    pay_what_you_want_price: options?.pay_what_you_want_price,
  });

  const existingIndex = cart.findIndex((item) => item.line_id === lineId);
  const quantityBeforeAdd = existingIndex >= 0 ? cart[existingIndex].quantity : 0;

  if (existingIndex >= 0) {
    cart[existingIndex].quantity += normalizedQuantity;

    if (options?.addons) {
      cart[existingIndex].addons = options.addons;
    }
    if (options?.custom_fields) {
      cart[existingIndex].custom_fields = options.custom_fields;
    }
    if (options?.price_variant_id) {
      cart[existingIndex].price_variant_id = options.price_variant_id;
    }
    if (options?.price_data) {
      cart[existingIndex].price_data = options.price_data;
    }
    if (options?.pay_what_you_want_price !== undefined) {
      cart[existingIndex].pay_what_you_want_price = options.pay_what_you_want_price;
    }
    applyDisplaySnapshot(cart[existingIndex], options);
    clampQuantityToBounds(cart[existingIndex]);
  } else {
    const pushed: CartItem = {
      line_id: lineId,
      product_id: productId,
      variant_id: variantId,
      quantity: normalizedQuantity,
      addons: options?.addons,
      custom_fields: options?.custom_fields,
      price_variant_id: options?.price_variant_id,
      price_data: options?.price_data,
      pay_what_you_want_price: options?.pay_what_you_want_price,
      title: options?.title,
      variant_title: options?.variant_title,
      image_url: options?.image_url,
      addon_labels: options?.addon_labels,
      max_quantity: options?.max_quantity,
      min_quantity: options?.min_quantity,
    };
    clampQuantityToBounds(pushed);
    cart.push(pushed);
  }

  const intendedQuantity = (existingIndex >= 0 ? cart[existingIndex] : cart[cart.length - 1]).quantity;
  writeCart(cart);
  // The storage wrapper swallows quota/availability failures, and the cart
  // and metadata writes can fail independently, so the funnel event is gated
  // on the LINE itself: the post-clamp quantity actually grew (a clamped
  // max-quantity no-op did not add anything) AND that exact quantity is
  // readable back from storage (the write of the cart payload landed).
  const persistedLine = getCart().find((item) => item.line_id === lineId);
  if (intendedQuantity > quantityBeforeAdd && persistedLine?.quantity === intendedQuantity) {
    // Attribute like checkout() does: a code applied to the cart (AFFILIATE
    // coupon source) outranks the ambient stored referral.
    const cartAffiliateCode = getCartCouponSource() === 'AFFILIATE' ? getCartCoupon() : null;
    void trackAffiliateEvent('add_to_cart', cartAffiliateCode ? { code: cartAffiliateCode } : undefined);
  }
}

// Cart-wide invariant: a line's quantity must stay within its stored [min_quantity, max_quantity]
// bounds. Every write path routes through this — addToCart (merge+push), setCartItem (merge+push),
// updateCartItem, mergeBaskets (merge+push), moveBasketItem (merge+push) — AND, crucially, the READ
// path normalizeCartItems (getCart) clamps too, so a stale/tampered/restored localStorage line is
// self-healed before it can reach checkout. No entrypoint can persist or serve a quantity outside the
// purchasable range the backend would reject.
// Order matters: apply min FIRST, then max — so on the degenerate min>max case (e.g. quantity_min 2 but
// only 1 in stock) MAX wins and the line is never pushed above the stock cap (over-stock is the harder
// backend reject than under-min). Never below 1. No-op for the bound(s) that are absent.
function clampQuantityToBounds(item: CartItem): void {
  const min = item.min_quantity;
  if (typeof min === 'number' && Number.isFinite(min) && item.quantity < min) {
    item.quantity = Math.floor(min);
  }
  const max = item.max_quantity;
  if (typeof max === 'number' && Number.isFinite(max) && item.quantity > max) {
    item.quantity = Math.floor(max);
  }
  if (item.quantity < 1) {
    item.quantity = 1;
  }
}

// Copy the optional display snapshot fields from add-options onto an existing line. Display-only —
// kept separate from the pricing/identity fields so the snapshot logic is identical for add and set.
function applyDisplaySnapshot(item: CartItem, options?: CartAddOptions): void {
  // Authoritative full re-snapshot (buy-box producer): replace ALL display fields verbatim, including
  // CLEARING ones the new snapshot omits — a removed add-on or a lifted cap must not leave stale data.
  if (options?.replace_display_snapshot) {
    item.title = options.title;
    item.variant_title = options.variant_title;
    item.image_url = options.image_url;
    item.addon_labels = options.addon_labels;
    item.max_quantity = options.max_quantity;
    item.min_quantity = options.min_quantity;
    return;
  }
  // Partial direct SDK call: only overwrite the fields actually provided.
  if (options?.title !== undefined) {
    item.title = options.title;
  }
  if (options?.variant_title !== undefined) {
    item.variant_title = options.variant_title;
  }
  if (options?.image_url !== undefined) {
    item.image_url = options.image_url;
  }
  if (options?.addon_labels !== undefined) {
    item.addon_labels = options.addon_labels;
  }
  if (options?.max_quantity !== undefined) {
    item.max_quantity = options.max_quantity;
  }
  if (options?.min_quantity !== undefined) {
    item.min_quantity = options.min_quantity;
  }
}

export function setCartItem(
  productId: string,
  variantId: string,
  quantity: number = 1,
  options?: CartAddOptions
): void {
  if (!productId || !variantId) {
    throw new CartError('product_id and variant_id are required');
  }

  const normalizedQuantity = normalizeQuantity(quantity);
  if (normalizedQuantity < 1) {
    throw new CartError('quantity must be at least 1');
  }

  const cart = getCart();
  const lineId = computeCartLineId({
    product_id: productId,
    variant_id: variantId,
    addons: options?.addons,
    custom_fields: options?.custom_fields,
    price_variant_id: options?.price_variant_id,
    price_data: options?.price_data,
    pay_what_you_want_price: options?.pay_what_you_want_price,
  });

  // Buy-now / replace semantics: one visible line per (product_id, variant_id). Drop any prior
  // line_ids for that pair so a config change (addons, price_data) does not leave a stale sibling.
  for (let index = cart.length - 1; index >= 0; index -= 1) {
    if (cart[index].product_id === productId && cart[index].variant_id === variantId) {
      cart.splice(index, 1);
    }
  }

  const pushed: CartItem = {
    line_id: lineId,
    product_id: productId,
    variant_id: variantId,
    quantity: normalizedQuantity,
    addons: options?.addons,
    custom_fields: options?.custom_fields,
    price_variant_id: options?.price_variant_id,
    price_data: options?.price_data,
    pay_what_you_want_price: options?.pay_what_you_want_price,
    title: options?.title,
    variant_title: options?.variant_title,
    image_url: options?.image_url,
    addon_labels: options?.addon_labels,
    max_quantity: options?.max_quantity,
    min_quantity: options?.min_quantity,
  };
  clampQuantityToBounds(pushed);
  cart.push(pushed);

  writeCart(cart);
}

/**
 * Patch one cart line.
 *
 * TRI-STATE BOUNDS: `min_quantity`/`max_quantity` accept `null` to DELETE the
 * stored bound — see {@link CartItemUpdate}. `undefined` (or an absent key)
 * leaves it alone, which is why a removed ceiling needs its own spelling.
 *
 * NO-OP UPDATES DO NOT WRITE. An update that leaves the cart byte-identical
 * returns without touching storage: no metadata bump, no `version` increment,
 * and — because the storage write is what other tabs observe — no `storage`
 * event. Writing anyway made an ineffective heal indistinguishable from a real
 * cart change, and two tabs healing the same line could hand the event back and
 * forth indefinitely. Line-shape healing of a legacy stored row is NOT this
 * function's job: `normalizeCartItems` clamps and repairs on every READ, which
 * is the chokepoint every access already flows through.
 */
export function updateCartItem(
  lineId: string,
  updates: CartItemUpdate
): void {
  const cart = getCart();
  const normalizedLineId = lineId.trim();
  if (!normalizedLineId) {
    throw new CartError('line_id is required');
  }

  const index = cart.findIndex((item) => item.line_id === normalizedLineId);

  if (index < 0) {
    throw new CartError('Item not found in cart');
  }

  // The exact bytes the result is compared against, taken BEFORE any mutation.
  const before = JSON.stringify(cart);

  if (updates.quantity !== undefined) {
    const normalizedQuantity = normalizeQuantity(updates.quantity);
    if (normalizedQuantity < 1) {
      cart.splice(index, 1);
      writeCart(cart);
      return;
    }
    cart[index].quantity = normalizedQuantity;
  }

  if (updates.addons !== undefined) {
    cart[index].addons = updates.addons;
  }

  if (updates.custom_fields !== undefined) {
    cart[index].custom_fields = updates.custom_fields;
  }

  if (updates.price_variant_id !== undefined) {
    cart[index].price_variant_id = updates.price_variant_id;
  }
  if (updates.price_data !== undefined) {
    cart[index].price_data = updates.price_data;
  }
  if (updates.pay_what_you_want_price !== undefined) {
    cart[index].pay_what_you_want_price = updates.pay_what_you_want_price;
  }
  // Tri-state: `null` is the merchant's bound being REMOVED, and it has to
  // delete the stored key — a bound that can only be raised and lowered but
  // never cleared outlives the limit it describes.
  if (updates.max_quantity !== undefined) {
    if (updates.max_quantity === null) delete cart[index].max_quantity;
    else cart[index].max_quantity = updates.max_quantity;
  }
  if (updates.min_quantity !== undefined) {
    if (updates.min_quantity === null) delete cart[index].min_quantity;
    else cart[index].min_quantity = updates.min_quantity;
  }
  clampQuantityToBounds(cart[index]);

  // Pricing and configuration fields are part of a cart line's identity.
  // Recompute after every update and merge if the new identity already exists.
  const nextLineId = computeCartLineId(cart[index]);
  const duplicateIndex = cart.findIndex((item, itemIndex) => itemIndex !== index && item.line_id === nextLineId);
  if (duplicateIndex >= 0) {
    cart[duplicateIndex].quantity += cart[index].quantity;
    clampQuantityToBounds(cart[duplicateIndex]);
    cart.splice(index, 1);
  } else {
    cart[index].line_id = nextLineId;
  }

  // Nothing moved: writing would bump `version`/`last_modified` and emit a
  // storage event that says the cart changed when it did not.
  if (JSON.stringify(cart) === before) return;

  writeCart(cart);
}

export function removeFromCart(lineId: string): void {
  const normalizedLineId = lineId.trim();
  if (!normalizedLineId) {
    throw new CartError('line_id is required');
  }

  const cart = getCart();
  const filtered = cart.filter((item) => item.line_id !== normalizedLineId);
  writeCart(filtered);
}

/**
 * Discards the cart entirely — and with it the proof that describes it.
 *
 * PAYMENT PATH. `latestQuoteToken` is evidence about a specific cart, and this
 * is the one mutation after which that cart does not exist in any form. Held
 * across the clear, the proof outlives its subject: a caller that clears and
 * re-adds (Buy Now REPLACES the cart) hands `/from-cart` a token bound to the
 * previous cart's hash, which is refused as `quote_token_stale` — a hand-off
 * dead-ended by a proof nobody asked for. And with no re-quote in between there
 * is nothing to replace it with, so the retry is refused identically.
 *
 * Only this path clears it. Every OTHER mutation turns one cart into another
 * cart, where a proof that no longer matches is the server's to refuse: it
 * answers `quote_token_stale`, the surfaces re-quote, and the buyer approves the
 * new total. Dropping the proof there instead would silently remove the approved
 * -total ceiling from a checkout that raced the re-quote, which is the failure
 * the token exists to prevent.
 */
export function clearCart(): void {
  removeItem(getStorageKey('cart'));
  removeItem(getStorageKey('meta'));
  clearCartCoupon();
  clearLatestQuoteToken();
}

export function createCartBackup(): void {
  const cart = getCart();
  setItem(getStorageKey('cartBackup'), cart);
  const cartCode = getStoredCartCode();
  if (cartCode) {
    setItem(getStorageKey('couponBackup'), cartCode);
  } else {
    removeItem(getStorageKey('couponBackup'));
  }
  const metadata = getCartMetadata();
  if (metadata) {
    setItem(getStorageKey('metaBackup'), metadata);
  } else {
    const now = Date.now();
    setItem(getStorageKey('metaBackup'), {
      created_at: now,
      last_modified: now,
      version: 1,
      checksum: computeChecksum(cart),
    });
  }
}

export function restoreCartFromBackup(): boolean {
  const backupRaw = getItem<unknown>(getStorageKey('cartBackup'));
  const backup = normalizeCartItems(backupRaw);
  const backupMeta = getItem<CartMetadata>(getStorageKey('metaBackup'));
  const backupCoupon = getItem<unknown>(getStorageKey('couponBackup'));

  if (backup && backup.length > 0) {
    setCartWithMetadata(backup, backupMeta);
    if (backupCoupon && typeof backupCoupon === 'object' && !Array.isArray(backupCoupon)) {
      const record = backupCoupon as Record<string, unknown>;
      const source = record.source === 'COUPON' || record.source === 'AFFILIATE'
        ? record.source
        : null;
      if (typeof record.code === 'string' && source) {
        setCartCoupon(record.code, source);
      } else {
        clearCartCoupon();
      }
    } else {
      clearCartCoupon();
    }
    return true;
  }

  return false;
}

export function mergeBaskets(items: CartBasketMergeLine[]): CartItem[] {
  const cart = getCart();

  for (const incoming of items) {
    const productId = typeof incoming.product_id === 'string' ? incoming.product_id.trim() : '';
    const variantId = typeof incoming.variant_id === 'string' ? incoming.variant_id.trim() : '';
    if (!productId || !variantId) {
      continue;
    }

    let normalizedQuantity: number;
    try {
      normalizedQuantity = normalizeQuantity(incoming.quantity);
    } catch {
      continue;
    }

    if (normalizedQuantity < 1) {
      continue;
    }

    const mergedLine = ensureCartLineId({
      ...incoming,
      line_id: incoming.line_id ?? computeCartLineId(incoming),
      product_id: productId,
      variant_id: variantId,
      quantity: normalizedQuantity,
    });
    const index = cart.findIndex((item) => item.line_id === mergedLine.line_id);

    if (index >= 0) {
      cart[index].quantity = Math.max(cart[index].quantity, normalizedQuantity);
      clampQuantityToBounds(cart[index]);
    } else {
      clampQuantityToBounds(mergedLine);
      cart.push(mergedLine);
    }
  }

  writeCart(cart);
  return cart;
}

export function moveBasketItem(
  fromProductId: string,
  fromVariantId: string,
  toProductId: string,
  toVariantId: string
): void {
  const cart = getCart();
  const fromIndex = cart.findIndex(
    (item) => item.product_id === fromProductId && item.variant_id === fromVariantId
  );

  if (fromIndex < 0) {
    throw new CartError('Item not found in cart');
  }

  const [fromItem] = cart.splice(fromIndex, 1);
  const toLineId = computeCartLineId({
    ...fromItem,
    product_id: toProductId,
    variant_id: toVariantId,
  });
  const toIndex = cart.findIndex((item) => item.line_id === toLineId);

  if (toIndex >= 0) {
    cart[toIndex].quantity += fromItem.quantity;
    clampQuantityToBounds(cart[toIndex]);
  } else {
    const moved = ensureCartLineId({
      ...fromItem,
      product_id: toProductId,
      variant_id: toVariantId,
      line_id: toLineId,
    });
    clampQuantityToBounds(moved);
    cart.push(moved);
  }

  writeCart(cart);
}

export function validateCartIntegrity(): boolean {
  const cart = getCart();
  const metadata = getCartMetadata();
  const checksum = computeChecksum(cart);

  if (!metadata) {
    setCartWithMetadata(cart, null);
    return true;
  }

  return metadata.checksum === checksum;
}

export function getCartStats(): CartStats {
  const cart = getCart();
  const integrityValid = validateCartIntegrity();
  const metadata = getCartMetadata();
  const backup = getItem<CartItem[]>(getStorageKey('cartBackup')) ?? [];
  const hasCompletePriceSnapshots =
    cart.length > 0 &&
    cart.every((item) => typeof item.price_data?.unit_price === 'number');
  const totalPrice = cart.reduce((sum, item) => {
    const unitPrice = item.price_data?.unit_price ?? 0;
    return sum + roundPayableAmount(unitPrice * item.quantity);
  }, 0);

  return {
    item_count: cart.length,
    total_quantity: cart.reduce((sum, item) => sum + item.quantity, 0),
    last_modified: metadata?.last_modified ?? 0,
    version: metadata?.version ?? 0,
    has_backup: backup.length > 0,
    integrity_valid: integrityValid,
    total_price: roundPayableAmount(totalPrice),
    total_price_is_estimate: cart.length > 0 && !hasCompletePriceSnapshots,
  };
}

export function getCartPayload(coupon?: string): CartPayload {
  const config = getConfig();
  const normalizedCoupon = normalizeCouponCode(coupon) ?? getCartCoupon() ?? undefined;
  return {
    store_slug: config.storeSlug,
    items: getCart(),
    coupon: normalizedCoupon,
  };
}

/**
 * The ambient referral code checkout() will submit as `affiliate_code`.
 *
 * Read exactly like checkout()'s normalizeAffiliateCode ambient branch: the
 * referral code the cart itself carries (source `AFFILIATE`) takes precedence
 * over the stored `?ref=` attribution. Sending it with the quote is what makes
 * the displayed total equal the amount the invoice will charge — without it a
 * cart holding both a coupon and a `?ref=` attribution quotes high.
 */
function getAmbientAffiliateCodeForQuote(): string | null {
  const storedCode = getCartCoupon();
  const storedSource = getCartCouponSource();
  if (storedSource === 'AFFILIATE' && storedCode) {
    return storedCode.trim().toLowerCase();
  }
  return getAffiliateCode();
}

/**
 * The proof from the most recent successful quote, held in memory only.
 *
 * Module-scoped rather than persisted: the token is evidence about a cart as
 * it was priced moments ago, and a stale one from a previous page load is
 * simply ignored by the server. Not exported as public API — `checkout()`
 * reads it, callers do not have to know it exists.
 */
let latestQuoteToken: string | null = null;

/**
 * Which quote call the held token belongs to.
 *
 * Quote responses settle in ARRIVAL order, not request order: two overlapping
 * `quoteCart` calls (cart A, then cart A+B) can answer with the OLDER one last,
 * and the token stored is then evidence about a cart the buyer no longer has.
 *
 * That is not a mispricing — `/from-cart` binds the token to a hash of the cart
 * content, currency, shop and submitted codes (`isCartQuoteTokenApplicable`),
 * so a superseded token simply fails to apply and is treated as ABSENT, and the
 * proof is a one-directional CEILING that can never lower a price. What it
 * costs is the ceiling itself: the buyer silently loses the protection for the
 * rest of that checkout. Keeping the newest call's answer is what keeps the
 * proof present for the cart actually on screen.
 */
let quoteSequence = 0;

/** @internal — read by checkout(); exported only across module boundaries. */
export function getLatestQuoteToken(): string | null {
  return latestQuoteToken;
}

/** @internal — test seam and cart-reset hook. */
export function clearLatestQuoteToken(): void {
  latestQuoteToken = null;
  // A quote already in flight must not resurrect the proof this cleared.
  quoteSequence += 1;
}

export async function quoteCart(coupon?: string, currency?: string) {
  const sequence = (quoteSequence += 1);
  const payload = getCartPayload(coupon);
  const normalizedCurrency = normalizeRequestedCurrency(currency)
    ?? resolveBuyerCurrency(getConfig().currency);
  const response = await post<CartQuote>('/v1/storefront/cart/quote', {
    shop_slug: payload.store_slug,
    cart: payload.items,
    coupon: payload.coupon,
    affiliate_code: getAmbientAffiliateCodeForQuote() ?? undefined,
    currency: normalizedCurrency ?? undefined,
  });

  // Only a successful quote replaces the held proof — a failed quote must not
  // clear a still-valid one.
  //
  // A SUCCESSFUL quote that carries no token clears it, rather than leaving the
  // previous one in place. The proof is evidence about one specific priced
  // cart, and `/from-cart` refuses a submitted proof it cannot apply
  // (`quote_token_stale` / `_invalid`) instead of ignoring it. So a held token
  // that this quote did not reissue is not a harmless leftover: every later
  // `checkout()` submits it, is refused, and re-quoting never helps because a
  // tokenless answer used to leave it untouched — a dead end the buyer cannot
  // escape from inside the cart.
  //
  // And only the NEWEST call may replace it: a slower earlier quote answering
  // last would otherwise overwrite the current cart's proof with one bound to a
  // cart hash that no longer exists.
  if (sequence !== quoteSequence) return response;
  if (response.success) {
    latestQuoteToken = typeof response.data?.quote_token === 'string'
      ? response.data.quote_token
      : null;
  }

  return response;
}

export function serializeCart(coupon?: string): string {
  const payload = getCartPayload(coupon);
  const json = JSON.stringify(payload);
  // UTF-8 safe Base64: encodeURIComponent converts to UTF-8, unescape converts percent-encoding to bytes
  if (typeof btoa !== 'undefined') {
    return btoa(unescape(encodeURIComponent(json)));
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(json, 'utf8').toString('base64');
  }
  throw new CartError('Base64 encoding is not available in this environment');
}
